import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { Appbar } from '@Components/app-bar';
import {
  useCultureTranslationsQuery,
  useSaveCultureTranslationsMutation,
} from '@Store/api/translationsSlice';
import { ITranslationItem, TranslationScope } from '@Types/Translation';
import { LOCALIZATION_PAGE } from '@Routes/constants';

const SCOPE_OPTIONS: { value: TranslationScope; label: string }[] = [
  { value: TranslationScope.Server, label: 'Server' },
  { value: TranslationScope.Mobile, label: 'Mobile' },
  { value: TranslationScope.Both, label: 'Both' },
];

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const FormContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 30px;
  width: 100%;
`;

const RowsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const BackButton = styled(Button)`
  text-transform: capitalize;
`;

export function EditTranslations() {
  const { cultureId } = useParams<{ cultureId: string }>();
  const navigate = useNavigate();

  const { data, isFetching } = useCultureTranslationsQuery(cultureId ?? '', {
    skip: !cultureId,
  });
  const [saveCultureTranslations, { isLoading: isSaving }] =
    useSaveCultureTranslationsMutation();

  const [rows, setRows] = useState<ITranslationItem[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (data?.translations) {
      setRows(data.translations);
    }
  }, [data]);

  const visibleIndexes = useMemo(() => {
    const term = search.trim().toLowerCase();
    
    if (!term) {
      return rows.map((_, index) => index);
    }

    return rows
      .map((_, index) => index)
      .filter((index) => {
        const row = rows[index];

        return (
          row.key.toLowerCase().includes(term) ||
          (row.value ?? '').toLowerCase().includes(term)
        );
      });
  }, [rows, search]);

  const handleBack = () => {
    navigate(LOCALIZATION_PAGE);
  };

  const handleKeyChange = (index: number, key: string) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, key } : row))
    );
  };

  const handleValueChange = (index: number, value: string) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, value } : row))
    );
  };

  const handleScopeChange = (index: number, scope: TranslationScope) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, scope } : row))
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      { key: '', value: '', scope: TranslationScope.Server },
    ]);
    // Clear any active filter so the new blank row is immediately visible.
    setSearch('');
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!cultureId) {
      return;
    }

    const trimmedRows = rows
      .map((row) => ({
        key: row.key.trim(),
        value: row.value ?? '',
        scope: row.scope,
      }))
      .filter((row) => row.key.length > 0);

    const keys = trimmedRows.map((row) => row.key);

    if (new Set(keys).size !== keys.length) {
      toast.error('Translation keys must be unique');

      return;
    }

    try {
      await saveCultureTranslations({
        cultureId,
        translations: trimmedRows,
      }).unwrap();
      toast.success('Translations saved');
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || 'Something went wrong...';
      toast.error(message);
    }
  };

  return (
    <Container>
      <Appbar
        subTitle={data?.cultureCode}
        title={data ? `Translations – ${data.displayName}` : 'Translations'}
      >
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Localization
        </BackButton>
      </Appbar>

      {isFetching ? (
        <Box sx={{ p: '30px' }}>Loading translations...</Box>
      ) : (
        <FormContainer>
          <TextField
            placeholder='Search by key or value'
            size='small'
            sx={{ mb: '20px', maxWidth: 400 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
          />

          {search.trim() ? (
            <Typography sx={{ mb: '12px' }} variant='body2'>
              {visibleIndexes.length} of {rows.length} keys match &quot;{search}&quot;
            </Typography>
          ) : null}

          <RowsContainer>
            {visibleIndexes.length === 0 ? (
              <Typography color='text.secondary' variant='body2'>
                No keys or values match your search.
              </Typography>
            ) : null}
            {visibleIndexes.map((index) => {
              const row = rows[index];

              return (
                <Row key={index}>
                  <TextField
                    placeholder='Key'
                    size='small'
                    sx={{ flex: 0.3 }}
                    value={row.key}
                    onChange={(e) => handleKeyChange(index, e.target.value)}
                  />
                  <TextField
                    multiline
                    placeholder='Value'
                    size='small'
                    sx={{ flex: 0.5 }}
                    value={row.value ?? ''}
                    onChange={(e) => handleValueChange(index, e.target.value)}
                  />
                  <Select
                    size='small'
                    sx={{ flex: 0.2 }}
                    value={row.scope ?? TranslationScope.Server}
                    onChange={(e) =>
                      handleScopeChange(index, Number(e.target.value) as TranslationScope)
                    }
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <IconButton onClick={() => handleRemoveRow(index)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Row>
              );
            })}
          </RowsContainer>

          <Button
            startIcon={<AddIcon />}
            sx={{ mt: '20px', textTransform: 'capitalize', alignSelf: 'flex-start' }}
            onClick={handleAddRow}
          >
            Add Key
          </Button>

          <Button
            disabled={isSaving}
            sx={{ mt: '20px', textTransform: 'capitalize', fontSize: 16, width: 240 }}
            variant='contained'
            onClick={handleSave}
          >
            Save Translations
          </Button>
        </FormContainer>
      )}
    </Container>
  );
}
